import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, X, CheckCircle2, AlertTriangle, FileText, Loader2,
  ChevronLeft, CheckSquare, Square, Plus, Printer
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useRepProfile } from '@/lib/RepProfileContext';
import { api, ApiError, getBackend } from '@/api/apiClient';
import { enqueue } from '@/lib/outbox';
import { notifyEnqueued } from '@/lib/syncEngine';
import { Collection } from '@/api/localStore';
import { cur } from '@/lib/currency';
import { todayLocalISO } from '@/lib/dates';
import { formatDate } from '@/lib/companyConfig';
import { useOdooAuth } from '@/lib/OdooAuthContext';
import DocDateField from '@/components/shared/DocDateField';

const STEPS = ['العميل', 'الفواتير', 'طريقة الدفع', 'التأكيد'];

// Odoo. On SellGo's own database the methods come from the company's settings.
const METHODS = [
  { key: 'cash', label: 'نقدي', icon: '💵', desc: 'دفع فوري' },
  { key: 'check', label: 'شيك', icon: '📝', desc: 'بيانات الشيك مطلوبة' },
  { key: 'bank', label: 'تحويل بنكي', icon: '🏦', desc: 'رقم مرجع مطلوب' },
  { key: 'wallet', label: 'محفظة إلكترونية', icon: '📱', desc: 'فودافون كاش / إنستاباي' },
];

function fmt(n) { return Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function OverdueBadge({ days }) {
  if (!days) return <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md">لم يحل</span>;
  if (days > 90) return <span className="text-xs text-red-700 bg-red-50 px-1.5 py-0.5 rounded-md font-bold">🔴 متأخر {days} يوم</span>;
  if (days > 60) return <span className="text-xs text-orange-700 bg-orange-50 px-1.5 py-0.5 rounded-md">🟠 متأخر {days} يوم</span>;
  if (days > 30) return <span className="text-xs text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-md">🟡 متأخر {days} يوم</span>;
  return <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-md">⏳ متأخر {days} يوم</span>;
}

export default function NewCollection() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { profile: repProfile, isMethodAllowed } = useRepProfile();
  const { odooSession } = useOdooAuth();

  // SellGo's own database: the methods the manager ticked in Settings — the
  // server lists only those this user may collect with — and each payment is
  // booked on the method picked.
  const sellgoDb = getBackend() === 'supabase';
  // …and managers / supervisors may date a collection back or ahead (a rep's
  // is the day it's written).
  const canPickDate = sellgoDb && ['manager', 'supervisor'].includes(odooSession?.role);
  const [payDate, setPayDate] = useState(todayLocalISO);
  const [dbMethods, setDbMethods] = useState(null);   // null while loading, false if it failed
  useEffect(() => {
    if (!sellgoDb) return;
    api.get('/sellgo_payment_journals')
      .then(res => setDbMethods((res?.data || []).map(m => ({
        key: String(m.id), id: m.id, label: m.name, icon: m.icon, desc: m.hint, kind: m.kind,
      }))))
      .catch(() => setDbMethods(false));
  }, [sellgoDb]);

  // The user can pick from methods their profile allows. Reps with a
  // cash-only profile will see only the "نقدي" option (and the picker
  // collapses to a single, pre-selected card).
  const availableMethods = useMemo(
    () => (sellgoDb ? (dbMethods || []) : METHODS.filter(m => isMethodAllowed(m.key))),
    [sellgoDb, dbMethods, isMethodAllowed]
  );

  const preCustomerId = searchParams.get('customer_id');
  const preCustomerName = searchParams.get('customer_name');

  const [step, setStep] = useState(1);

  // Step 1
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(
    preCustomerId ? { id: parseInt(preCustomerId), name: preCustomerName || '' } : null
  );
  const [customerData, setCustomerData] = useState(null); // { customer, summary, invoices }
  const [loadingCustomerData, setLoadingCustomerData] = useState(false);
  const searchTimer = useRef(null);

  // Step 2
  const [selectedInvoices, setSelectedInvoices] = useState({}); // id → amount
  const [partialAmounts, setPartialAmounts] = useState({}); // id → custom amount string
  const [advanceAmount, setAdvanceAmount] = useState(''); // user-entered advance payment

  // Step 3
  const [method, setMethod] = useState('cash');
  const selectedMethod = availableMethods.find(m => m.key === method)
    ?? (sellgoDb ? undefined : METHODS.find(m => m.key === method));
  // What the method needs: cheque details, a reference, or nothing (cash).
  const methodKind = sellgoDb ? selectedMethod?.kind : method;
  const [reference, setReference] = useState('');
  const [chequeNumber, setChequeNumber] = useState('');
  const [chequeBank, setChequeBank] = useState('');
  const [chequeDate, setChequeDate] = useState('');
  const [notes, setNotes] = useState('');

  // Submit
  const [saving, setSaving] = useState(false);
  // Synchronous re-entry guard. `saving` (state) updates asynchronously, so a
  // very fast double-tap can fire save() twice before the button re-renders as
  // disabled — creating two identical payments (the customer gets charged
  // twice). A ref flips synchronously and blocks the second call immediately.
  const savingRef = useRef(false);
  const [done, setDone] = useState(null);

  // Load customer data
  const loadCustomerData = async (customer) => {
    setLoadingCustomerData(true);
    const res = await api.get(`/collections/customer/${customer.id}/invoices`);
    if (res?.success) {
      setCustomerData({ invoices: res.data || [], summary: res.summary, customer: res.customer || customer });
    }
    setLoadingCustomerData(false);
  };

  // Reset the selected method to one the profile actually allows, in
  // case the default ("cash") was disabled for this rep.
  useEffect(() => {
    if (availableMethods.length === 0) return;
    if (!availableMethods.some(m => m.key === method)) {
      setMethod(availableMethods[0].key);
    }
  }, [availableMethods, method]);

  // Prefetch on mount if a customer was pre-selected (e.g. arriving from an
  // order's "تحصيل" button with ?customer_id=...). We mirror selectCustomer():
  // load the customer's invoices AND skip the picker by jumping to step 2,
  // so the user lands on the invoices screen with the customer already set
  // instead of an empty picker.
  useEffect(() => {
    if (selectedCustomer) {
      loadCustomerData(selectedCustomer);
      setStep(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCustomers = async (val) => {
    setLoadingCustomers(true);
    // Use '/sellgo_partners' — it supports server-side search by
    // name/phone AND scopes by the rep's dealer categories (falling back to
    // all customers when the rep has none). The legacy '/customers' endpoint
    // ignores the search term, so typing a name returned the wrong list.
    const params = { search: val || undefined, limit: 50 };
    let res;
    try {
      res = await api.get('/sellgo_v5_customer_search', params);
    } catch {
      res = await api.get('/customers', params);
    }
    setCustomerResults(res?.success ? (res.data || []) : []);
    setLoadingCustomers(false);
  };

  const handleCustomerSearch = (val) => {
    setCustomerSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchCustomers(val), 250);
  };

  // Show the first batch of customers as soon as the picker opens —
  // the rep should see "their" customers immediately without having
  // to type anything.
  useEffect(() => {
    if (step === 1 && !customerSearch && customerResults.length === 0 && !selectedCustomer) {
      fetchCustomers('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const selectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch('');
    setCustomerResults([]);
    setSelectedInvoices({});
    setPartialAmounts({});
    await loadCustomerData(customer);
    setStep(2);
  };

  // Invoice selection
  const toggleInvoice = (inv) => {
    setSelectedInvoices(prev => {
      const next = { ...prev };
      if (next[inv.id]) delete next[inv.id];
      else next[inv.id] = inv.amount_residual || 0;
      return next;
    });
  };

  const selectAll = () => {
    const all = {};
    (customerData?.invoices || []).forEach(inv => { all[inv.id] = inv.amount_residual || 0; });
    setSelectedInvoices(all);
  };

  const selectOldestFirst = () => {
    const sorted = [...(customerData?.invoices || [])].sort((a, b) => (b.overdue_days || 0) - (a.overdue_days || 0));
    const first3 = {};
    sorted.slice(0, 3).forEach(inv => { first3[inv.id] = inv.amount_residual || 0; });
    setSelectedInvoices(first3);
  };

  const setPartialAmount = (invId, val) => {
    setPartialAmounts(prev => ({ ...prev, [invId]: val }));
    setSelectedInvoices(prev => ({ ...prev, [invId]: parseFloat(val) || 0 }));
  };

  const advanceNum = parseFloat(advanceAmount) || 0;
  const invoicesTotal = Object.values(selectedInvoices).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const totalSelected = invoicesTotal + advanceNum;
  const selectedCount = Object.keys(selectedInvoices).length;
  // Can proceed if either: at least one invoice is selected, OR an advance
  // amount > 0 was entered. This is what unlocks "pay even without an invoice".
  const canProceed = selectedCount > 0 || advanceNum > 0;

  // Submit
  const save = async () => {
    if (!selectedCustomer || !canProceed || totalSelected <= 0) return;
    if (savingRef.current) return;   // a save is already in flight — ignore the extra tap
    savingRef.current = true;
    setSaving(true);

    const refStr = methodKind === 'check' ? (chequeNumber || reference) : reference;
    const payDay = canPickDate ? payDate : todayLocalISO();

    // Build the list of invoice IDs the user actually selected. The
    // backend reconciles the payment against these so the invoice's
    // residual drops accordingly. An "advance only" payment leaves
    // this list empty.
    const invoiceIds = Object.keys(selectedInvoices).map(id => parseInt(id)).filter(Boolean);

    // Resolve a journal for the payment. Prefer the one the admin pinned on
    // the rep's profile; if none is pinned, fetch an allowed journal from the
    // backend. We MUST go through '/sellgo_payment_create' (not the legacy v4
    // '/collections/payments'), because the v4 endpoint posts the payment under
    // sudo() — so its create_uid becomes the superuser, NOT the rep. That makes
    // the payment invisible in "محصل اليوم" and "التحصيلات المسجلة", which both
    // filter by create_uid == the rep. The SellGo endpoint runs as the rep, so
    // attribution is correct.
    let journalId = sellgoDb
      ? (selectedMethod?.id || false)
      : Array.isArray(repProfile.default_journal_id)
        ? repProfile.default_journal_id[0]
        : (repProfile.default_journal_id || false);

    if (!journalId && !sellgoDb) {
      // No pinned journal — ask the backend for an allowed one.
      try {
        const j = await api.get('/sellgo_payment_journals');
        const list = j?.data || [];
        journalId = (list[0] && (list[0].id || list[0][0])) || false;
      } catch {
        journalId = false;
      }
    }

    // Offline: save the payment to the outbox and sync later. The journal must
    // already be resolvable (pinned on the profile or cached), otherwise we
    // can't build a valid payment.
    const offlineBody = {
      partner_id: selectedCustomer.id,
      amount: totalSelected,
      journal_id: journalId,
      date: payDay,
      ref: refStr || '',
      communication: notes || '',
      invoice_ids: invoiceIds,
    };
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      if (!journalId) {
        toast.error('تعذّر تحديد دفتر اليومية بدون إنترنت — حضّر البيانات وانت متصل أولاً');
        savingRef.current = false;
        setSaving(false);
        return;
      }
      await enqueue('collection', { body: offlineBody });
      notifyEnqueued();
      setDone({ __queued: true, payment_name: 'في انتظار المزامنة', journal: '' });
      toast.success('تم حفظ التحصيل — هيترفع تلقائياً لما النت يرجع');
      savingRef.current = false;
      setSaving(false);
      return;
    }

    // Prefer the SellGo v6 endpoint — correct attribution + invoice settlement.
    // Fall back to the v4 endpoint ONLY if the SellGo endpoint isn't deployed
    // (404) or we genuinely couldn't resolve a journal at all.
    let res;
    let usedNewEndpoint = false;
    if (journalId) {
      try {
        res = await api.post('/sellgo_payment_create', {
          partner_id: selectedCustomer.id,
          amount: totalSelected,
          journal_id: journalId,
          date: payDay,
          ref: refStr || '',
          communication: notes || '',
          invoice_ids: invoiceIds,
        });
        usedNewEndpoint = !!res?.success;
      } catch (e) {
        // SellGo's own database has no older endpoint to fall back to: say why it was refused.
        if (sellgoDb && e instanceof ApiError && e.status) {
          toast.error(e.message);
          savingRef.current = false;
          setSaving(false);
          return;
        }
        usedNewEndpoint = false;
      }
    }
    if (!usedNewEndpoint) {
      try {
        res = await api.post('/collections/payments', {
          partner_id: selectedCustomer.id,
          amount: totalSelected,
          payment_date: payDay,
          memo: refStr || notes || '',
          invoice_ids: invoiceIds,    // v4 may or may not use this; harmless
        });
      } catch (e) {
        // Network dropped mid-request → queue it (if we have a journal).
        if (journalId && e instanceof ApiError && (!e.status || e.status === 0)) {
          await enqueue('collection', { body: offlineBody });
          notifyEnqueued();
          setDone({ __queued: true, payment_name: 'في انتظار المزامنة', journal: '' });
          toast.success('تم حفظ التحصيل — هيترفع تلقائياً لما النت يرجع');
          savingRef.current = false;
          setSaving(false);
          return;
        }
        throw e;
      }
    }

    if (res?.success) {
      // Mirror to local Collections store for offline history
      try {
        const currentUser = (await api.get('/me')).data;
        await Collection.create({
          customer_id: selectedCustomer.id,
          customer_name: selectedCustomer.name,
          salesman_email: currentUser?.email,
          salesman_name: currentUser?.full_name || currentUser?.name,
          amount: totalSelected,
          payment_method: methodKind,
          notes,
          reference: res.payment_name || refStr,
          collection_date: new Date().toISOString(),
          status: 'synced',
          odoo_payment_id: res.payment_id,
        });
      } catch {
        // local mirror failure is non-fatal
      }
      setDone(res.data);
      toast.success('تم تسجيل التحصيل بنجاح');
    } else {
      toast.error(res?.error || 'فشل تسجيل التحصيل');
    }
    savingRef.current = false;
    setSaving(false);
  };

  // ── Success screen ────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] p-6 gap-6 text-center" dir="rtl">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', damping: 15 }}>
          <div className="w-24 h-24 bg-secondary/10 rounded-full flex items-center justify-center">
            <CheckCircle2 className="w-14 h-14 text-secondary" />
          </div>
        </motion.div>
        <div className="space-y-1">
          <h2 className="text-2xl font-bold text-foreground">{done.__queued ? 'تم حفظ التحصيل' : 'تم التحصيل بنجاح! 🎉'}</h2>
          <p className="text-3xl font-black text-secondary">{fmt(totalSelected)} {cur()}</p>
          {done.__queued ? (
            <p className="text-sm text-amber-600">هيترفع تلقائياً أول ما النت يرجع</p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">{done.payment_name}</p>
              {canPickDate && payDate !== todayLocalISO() && (
                <p className="text-xs font-semibold text-amber-600">بتاريخ {formatDate(`${payDate}T12:00:00`)}</p>
              )}
              {sellgoDb ? (
                <p className="text-xs text-muted-foreground">{selectedMethod?.icon} {selectedMethod?.label}</p>
              ) : (
                <p className="text-xs text-muted-foreground">دفتر يومية: {done.journal}</p>
              )}
            </>
          )}
        </div>
        <div className="grid grid-cols-1 gap-2 w-full max-w-xs">
          <Button
            onClick={() => navigate('/collections/print/receipt', {
              state: {
                collectionData: {
                  name: done.payment_name || 'سند قبض',
                  amount: totalSelected,
                  customer_name: selectedCustomer?.name || '',
                  partner_id: selectedCustomer ? [selectedCustomer.id, selectedCustomer.name] : false,
                  // a dated collection prints its own day (at the current time of day)
                  date: canPickDate && payDate !== todayLocalISO()
                    ? `${payDate}T${new Date().toTimeString().slice(0, 8)}`
                    : new Date().toISOString(),
                  payment_method: selectedMethod?.label || 'نقدي',
                  notes,
                  reference: chequeNumber || reference || '',
                },
              },
            })}
            className="bg-secondary rounded-xl h-11 gap-2">
            <Printer className="w-4 h-4" /> طباعة السند
          </Button>
          <Button onClick={() => { setDone(null); setSelectedInvoices({}); setStep(2); setPayDate(todayLocalISO()); loadCustomerData(selectedCustomer); }}
            variant="outline" className="rounded-xl h-11">تحصيل آخر لنفس العميل</Button>
          <Button onClick={() => { setDone(null); setSelectedCustomer(null); setCustomerData(null); setSelectedInvoices({}); setStep(1); setPayDate(todayLocalISO()); }}
            variant="outline" className="rounded-xl h-11">تحصيل لعميل آخر</Button>
          <Button onClick={() => navigate('/collections')} variant="ghost" className="rounded-xl h-11 text-muted-foreground">
            العودة للتحصيلات
          </Button>
        </div>
      </div>
    );
  }

  const invoices = customerData?.invoices || [];
  const summary = customerData?.summary;

  return (
    <div className="flex flex-col min-h-full" dir="rtl">
      {/* Step indicator */}
      <div className="sticky top-0 z-10 bg-background border-b border-border px-4 pt-4 pb-3">
        <div className="flex gap-1 mb-1.5">
          {STEPS.map((_, i) => (
            <div key={i} className={`flex-1 h-1.5 rounded-full transition-all ${step > i + 1 ? 'bg-secondary' : step === i + 1 ? 'bg-secondary/70' : 'bg-muted'}`} />
          ))}
        </div>
        <p className="text-xs text-muted-foreground text-center">
          الخطوة {step} من {STEPS.length} — <span className="font-semibold text-foreground">{STEPS[step - 1]}</span>
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-4 pb-28 space-y-4">
        <AnimatePresence mode="wait">

          {/* ── STEP 1: Customer ── */}
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="space-y-4">
              <div>
                <h2 className="font-black text-foreground text-xl mb-1">اختيار العميل</h2>
                <p className="text-xs text-muted-foreground">ابحث بالاسم أو رقم الهاتف، أو اختر من قائمة عملائك</p>
              </div>
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input value={customerSearch} onChange={e => handleCustomerSearch(e.target.value)}
                  placeholder="بحث بالاسم أو الهاتف..."
                  className="pr-11 h-12 bg-card rounded-2xl text-base border-2 focus:border-primary/40" autoFocus />
                {customerSearch && (
                  <button onClick={() => { setCustomerSearch(''); fetchCustomers(''); }}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {loadingCustomers && (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              )}
              {!loadingCustomers && customerResults.length === 0 && (
                <div className="flex flex-col items-center gap-2 py-10 text-center">
                  <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center">
                    <Search className="w-7 h-7 text-muted-foreground/60" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {customerSearch ? 'لا يوجد عميل مطابق' : 'لا يوجد عملاء في فئاتك'}
                  </p>
                </div>
              )}
              <div className="space-y-2">
                {customerResults.map(c => (
                  <button key={c.id} onClick={() => selectCustomer(c)}
                    className="w-full bg-card border border-border rounded-2xl p-3.5 flex items-center gap-3 hover:border-primary/40 hover:shadow-sm transition-all text-right">
                    <div className="w-11 h-11 bg-gradient-to-br from-secondary/15 to-secondary/5 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-secondary font-black text-lg">{c.name?.charAt(0) || '?'}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-foreground truncate">{c.name}</p>
                      {(c.phone || c.mobile || c.city) && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                          {(c.phone || c.mobile) && <span>📞 {c.phone || c.mobile}</span>}
                          {c.city && <span>📍 {c.city}</span>}
                        </div>
                      )}
                    </div>
                    <ChevronLeft className="w-5 h-5 text-muted-foreground/50 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* ── STEP 2: Invoices ── */}
          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="space-y-4">
              {/* Customer chip */}
              <div className="flex items-center gap-2 bg-secondary/5 border border-secondary/20 rounded-xl p-3">
                <div className="w-9 h-9 bg-secondary/15 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-secondary font-bold">{selectedCustomer?.name?.charAt(0)}</span>
                </div>
                <span className="font-bold text-foreground flex-1">{selectedCustomer?.name}</span>
                {!preCustomerId && (
                  <button onClick={() => { setSelectedCustomer(null); setCustomerData(null); setStep(1); }}
                    className="text-xs text-primary font-medium">تغيير</button>
                )}
              </div>

              {/* Customer summary */}
              {summary && (
                <div className="bg-card border border-border rounded-2xl p-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-bold text-foreground">إجمالي المستحق</p>
                    <p className="text-xl font-black text-foreground">{fmt(summary.total)} {cur()}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 text-xs">
                    {summary.b90plus > 0 && <div className="bg-red-50 rounded-lg p-2"><p className="text-red-700 font-bold">{fmt(summary.b90plus)} {cur()}</p><p className="text-red-500">🔴 +90 يوم</p></div>}
                    {summary.b60 > 0 && <div className="bg-orange-50 rounded-lg p-2"><p className="text-orange-700 font-bold">{fmt(summary.b60)} {cur()}</p><p className="text-orange-500">🟠 61-90 يوم</p></div>}
                    {summary.b30 > 0 && <div className="bg-amber-50 rounded-lg p-2"><p className="text-amber-700 font-bold">{fmt(summary.b30)} {cur()}</p><p className="text-amber-500">🟡 31-60 يوم</p></div>}
                    {summary.b0 > 0 && <div className="bg-emerald-50 rounded-lg p-2"><p className="text-emerald-700 font-bold">{fmt(summary.b0)} {cur()}</p><p className="text-emerald-500">🟢 0-30 يوم</p></div>}
                  </div>
                  {summary.last_payment && (
                    <p className="text-xs text-muted-foreground">آخر تحصيل: {new Date(summary.last_payment.date).toLocaleDateString('ar-EG')}</p>
                  )}
                </div>
              )}

              {loadingCustomerData ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-secondary" /></div>
              ) : invoices.length === 0 ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 flex-shrink-0 text-emerald-700 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-emerald-800">لا توجد فواتير مستحقة</p>
                    <p className="text-xs text-emerald-700 mt-0.5">يمكنك تسجيل دفعة مقدمة من القسم بالأسفل</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Quick select actions */}
                  <div className="flex gap-2">
                    <button onClick={selectAll} className="flex-1 py-2 bg-secondary/10 text-secondary rounded-xl text-xs font-bold hover:bg-secondary/20 transition-colors">
                      تحديد الكل ({invoices.length})
                    </button>
                    <button onClick={selectOldestFirst} className="flex-1 py-2 bg-muted rounded-xl text-xs font-bold text-muted-foreground hover:bg-muted/70 transition-colors">
                      الأقدم أولاً (3)
                    </button>
                    {selectedCount > 0 && (
                      <button onClick={() => setSelectedInvoices({})} className="flex-1 py-2 bg-muted rounded-xl text-xs font-bold text-muted-foreground hover:bg-muted/70 transition-colors">
                        إلغاء التحديد
                      </button>
                    )}
                  </div>

                  {/* Invoice list */}
                  <div className="space-y-2">
                    {invoices.map(inv => {
                      const isSelected = !!selectedInvoices[inv.id];
                      return (
                        <div key={inv.id} onClick={() => toggleInvoice(inv)}
                          className={`border-2 rounded-xl p-3 cursor-pointer transition-all ${isSelected ? 'border-secondary bg-secondary/5' : 'border-border bg-card hover:border-secondary/30'}`}>
                          <div className="flex items-start gap-2">
                            <div className="mt-0.5 flex-shrink-0">
                              {isSelected ? <CheckSquare className="w-4 h-4 text-secondary" /> : <Square className="w-4 h-4 text-muted-foreground" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-sm font-bold text-foreground">{inv.name}</p>
                                <p className="font-bold text-secondary text-sm whitespace-nowrap">{fmt(inv.amount_residual)} {cur()}</p>
                              </div>
                              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                <p className="text-xs text-muted-foreground">استحقاق: {inv.invoice_date_due || '—'}</p>
                                <OverdueBadge days={inv.overdue_days} />
                              </div>
                            </div>
                          </div>
                          {/* Partial amount input */}
                          {isSelected && (
                            <div className="mt-2 flex items-center gap-2" onClick={e => e.stopPropagation()}>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">المبلغ:</span>
                              <input type="number"
                                value={partialAmounts[inv.id] !== undefined ? partialAmounts[inv.id] : (inv.amount_residual || '')}
                                onChange={e => setPartialAmount(inv.id, e.target.value)}
                                className="flex-1 h-8 px-2 text-sm border border-secondary/50 rounded-lg bg-card focus:outline-none focus:ring-1 focus:ring-secondary/50"
                                max={inv.amount_residual} min={0.01} step={0.01}
                              />
                              <span className="text-xs text-muted-foreground">{cur()}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* ── Advance payment ── always available so the rep can
                  accept a deposit even when the customer has no open
                  invoices, or pay-on-account in addition to settling
                  invoices. */}
              {(repProfile.allow_advance_payment !== false) && (
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">💰</span>
                    <div className="flex-1">
                      <p className="font-bold text-amber-900 text-sm">دفعة مقدمة (على الحساب)</p>
                      <p className="text-xs text-amber-700">تُسجَّل كرصيد دائن للعميل بدون ربط بفاتورة محددة</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="number" min={0} step={0.01}
                      value={advanceAmount}
                      onChange={e => setAdvanceAmount(e.target.value)}
                      placeholder="0.00"
                      className="flex-1 h-11 px-3 text-base font-bold border-2 border-amber-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 text-right"
                    />
                    <span className="text-sm font-bold text-amber-800">{cur('ج.م')}</span>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* ── STEP 3: Payment method ── */}
          {step === 3 && (
            <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="space-y-4">
              <h2 className="font-bold text-foreground text-lg">طريقة الدفع</h2>
              <p className="text-2xl font-black text-secondary">{fmt(totalSelected)} {cur()}</p>

              {/* Big method buttons (filtered by rep profile) */}
              {sellgoDb && dbMethods === null && (
                <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-secondary" /></div>
              )}
              {sellgoDb && (dbMethods === false || dbMethods?.length === 0) && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-amber-800 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {dbMethods === false
                      ? 'تعذّر تحميل وسائل الدفع — اتصل بالإنترنت وافتح الشاشة تاني'
                      : 'مفيش وسائل دفع متاحة لك — المدير يفعّلها من الإعدادات ← وسائل الدفع'}
                  </span>
                </div>
              )}
              {availableMethods.length === 1 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-emerald-800 text-sm flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>تم اختيار الطريقة المتاحة فقط</span>
                </div>
              )}
              <div className={`grid gap-3 ${availableMethods.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {availableMethods.map(m => (
                  <button key={m.key} onClick={() => setMethod(m.key)}
                    className={`p-4 rounded-2xl border-2 flex flex-col items-center gap-2 transition-all ${method === m.key ? 'border-secondary bg-secondary/10 shadow-md' : 'border-border bg-card hover:border-secondary/40'}`}>
                    <span className="text-3xl">{m.icon}</span>
                    <span className={`text-sm font-bold ${method === m.key ? 'text-secondary' : 'text-foreground'}`}>{m.label}</span>
                    <span className="text-xs text-muted-foreground">{m.desc}</span>
                  </button>
                ))}
              </div>

              {/* Method-specific fields */}
              {(methodKind === 'check') && (
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-muted-foreground">رقم الشيك *</label>
                    <Input value={chequeNumber} onChange={e => setChequeNumber(e.target.value)} placeholder="رقم الشيك" className="rounded-xl" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-muted-foreground">اسم البنك</label>
                    <Input value={chequeBank} onChange={e => setChequeBank(e.target.value)} placeholder="مثال: البنك الأهلي" className="rounded-xl" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-muted-foreground">تاريخ الشيك</label>
                    <Input type="date" value={chequeDate} onChange={e => setChequeDate(e.target.value)} className="rounded-xl" />
                  </div>
                </div>
              )}
              {(methodKind === 'bank' || methodKind === 'wallet') && (
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">رقم المرجع / العملية</label>
                  <Input value={reference} onChange={e => setReference(e.target.value)} placeholder="رقم التحويل / المحفظة" className="rounded-xl" />
                </div>
              )}
              {(methodKind === 'card' || methodKind === 'other') && (
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">رقم العملية (اختياري)</label>
                  <Input value={reference} onChange={e => setReference(e.target.value)}
                    placeholder={methodKind === 'card' ? 'رقم إيصال الشبكة' : 'رقم المرجع'} className="rounded-xl" />
                </div>
              )}

              {canPickDate && <DocDateField label="تاريخ التحصيل" value={payDate} onChange={setPayDate} />}

              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground">ملاحظات (اختياري)</label>
                <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="ملاحظات إضافية..." rows={2} className="rounded-xl" />
              </div>
            </motion.div>
          )}

          {/* ── STEP 4: Review & Confirm ── */}
          {step === 4 && (
            <motion.div key="s4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="space-y-4">
              <h2 className="font-bold text-foreground text-lg">مراجعة وتأكيد</h2>

              {/* Summary hero */}
              <div className="bg-secondary/5 border border-secondary/20 rounded-2xl p-5 text-center">
                <p className="text-3xl font-black text-secondary">{fmt(totalSelected)} {cur()}</p>
                <p className="text-sm text-muted-foreground mt-1">{selectedCount} فاتورة</p>
              </div>

              <div className="bg-card border border-border rounded-2xl divide-y divide-border">
                <div className="flex justify-between px-4 py-3">
                  <span className="text-sm text-muted-foreground">العميل</span>
                  <span className="font-semibold text-foreground">{selectedCustomer?.name}</span>
                </div>
                <div className="flex justify-between px-4 py-3">
                  <span className="text-sm text-muted-foreground">طريقة الدفع</span>
                  <span className="font-semibold text-foreground">
                    {selectedMethod?.icon} {selectedMethod?.label}
                  </span>
                </div>
                {canPickDate && payDate !== todayLocalISO() && (
                  <div className="flex justify-between px-4 py-3">
                    <span className="text-sm text-muted-foreground">التاريخ</span>
                    <span className="font-semibold text-amber-600">{formatDate(`${payDate}T12:00:00`)}</span>
                  </div>
                )}
                {(reference || chequeNumber) && (
                  <div className="flex justify-between px-4 py-3">
                    <span className="text-sm text-muted-foreground">المرجع</span>
                    <span className="font-semibold text-foreground">{chequeNumber || reference}</span>
                  </div>
                )}
                {notes && (
                  <div className="flex justify-between px-4 py-3">
                    <span className="text-sm text-muted-foreground">ملاحظات</span>
                    <span className="font-semibold text-foreground text-left max-w-[60%]">{notes}</span>
                  </div>
                )}
              </div>

              {/* Invoice list */}
              <div className="bg-card border border-border rounded-2xl overflow-hidden">
                <div className="px-4 py-2 bg-muted/30 border-b border-border">
                  <p className="text-xs font-bold text-muted-foreground">الفواتير المشمولة</p>
                </div>
                {invoices.filter(inv => selectedInvoices[inv.id]).map(inv => (
                  <div key={inv.id} className="flex justify-between px-4 py-3 border-b border-border last:border-0">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{inv.name}</p>
                      <p className="text-xs text-muted-foreground">مستحق: {inv.invoice_date_due || '—'}</p>
                    </div>
                    <p className="font-bold text-secondary text-sm">{fmt(selectedInvoices[inv.id])} {cur()}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Sticky bottom bar ── */}
      <div className="fixed bottom-0 right-0 left-0 bg-card border-t border-border px-4 py-3 z-20 flex gap-2">
        {step > 1 && (
          <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={saving} className="rounded-xl h-11 px-4">
            رجوع
          </Button>
        )}

        {step === 1 && (
          <Button onClick={() => { if (selectedCustomer) setStep(2); }} disabled={!selectedCustomer} className="flex-1 bg-secondary rounded-xl h-11 font-bold">
            تابع
          </Button>
        )}

        {step === 2 && (
          <Button onClick={() => { if (canProceed) setStep(3); }} disabled={!canProceed || totalSelected <= 0}
            className="flex-1 bg-secondary rounded-xl h-11 font-bold gap-1.5">
            تابع — {fmt(totalSelected)} {cur()}
          </Button>
        )}

        {step === 3 && (
          <Button onClick={() => setStep(4)} disabled={sellgoDb && !selectedMethod} className="flex-1 bg-secondary rounded-xl h-11 font-bold">
            مراجعة الطلب
          </Button>
        )}

        {step === 4 && (
          <Button onClick={save} disabled={saving} className="flex-1 bg-secondary rounded-xl h-11 font-bold gap-1.5">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> جاري التسجيل...</> : '✅ تأكيد التحصيل'}
          </Button>
        )}
      </div>
    </div>
  );
}